import type { Compute, DynamicAttributeData, DynamicAttributeSnapshot, EnrichmentData, FieldCorrection, ImportedData, ManualCustomer, ManualPlatform, Platform } from '@/types'

/**
 * Case-invariant helpers. Alle identifiers/gebruikersinvoer wordt lowercase
 * opgeslagen en getoond (zie #1396). UUIDs, numerieke waarden, IP-adressen
 * en file-paths vallen buiten scope.
 *
 * Alle functies zijn pure en idempotent: `canon(canon(x)) === canon(x)`.
 * Referentie-gelijkheid blijft bewaard zodra een object al canonical is,
 * zodat React/Zustand geen onnodige rerenders triggeren.
 */

/** Trim + lowercase. Leeg/undefined → leeg. */
export function canonicalString(v: string | null | undefined): string {
  if (v === null || v === undefined) return ''
  return v.trim().toLowerCase()
}

// Leidend nummerings-/rubriek-prefix zoals `1. `, `2.`, `4, `, `X. ` — een kort
// alfanumeriek token (1-3 tekens) gevolgd door `.` of `,` en whitespace.
const ENV_LEADING_INDEX_RE = /^\s*[0-9a-z]{1,3}\s*[.,]\s*/i
// Redundant achtervoegsel `... environment` dat sommige bronnen aanhangen.
const ENV_TRAILING_WORD_RE = /\s+environment\s*$/i

/**
 * Structurele environment-normalisatie (#2151). Environment is onderdeel van de
 * platform-identiteit (`{environment}/{platformName}` → functionalKey → UUID),
 * dus spelling-/format-drift in de externe bron (`1. Production`, `production`,
 * `1. production environment`) mag niet tot een *andere* identiteit leiden.
 *
 * Deze laag vangt puur format-ruis af — leidend nummerings-prefix (`1. `, `X. `),
 * redundant ` environment`-achtervoegsel, whitespace en casing — zodat dezelfde
 * logische omgeving altijd dezelfde canonieke token oplevert. Echte semantische
 * aliassen (bv. NL `productie` ↔ EN `production`, of `dev` ↔ `development`)
 * vallen hier bewust buiten: die horen in de value-mapping/canonieke-env-config
 * (Unit B van #2151), niet in deze structurele laag.
 *
 * Idempotent en pure: `canonicalEnvironment(canonicalEnvironment(x)) === canonicalEnvironment(x)`.
 * Sentinels als `__unknown__` blijven ongemoeid (geen numeriek prefix, geen suffix).
 */
export function canonicalEnvironment(v: string | null | undefined): string {
  if (v === null || v === undefined) return ''
  let s = v.trim()
  if (s === '') return ''
  s = s.replace(ENV_LEADING_INDEX_RE, '')
  s = s.replace(ENV_TRAILING_WORD_RE, '')
  s = s.replace(/\s+/g, ' ').trim()
  // Fallback: als het strippen alles wegvreet (bv. input was enkel `1.`),
  // val terug op de enkel-lowercase vorm zodat we geen lege identiteit maken.
  if (s === '') return canonicalString(v)
  return s.toLowerCase()
}

/**
 * Genormaliseerde string-representatie van een array-veld: lowercase, gesorteerd,
 * met `,` gejoind. Gedeelde bron-van-waarheid voor multi-source velden zoals
 * `Compute.sources` — gebruikt door zowel de tabel-kolomdefs als de query-evaluator,
 * zodat `sources = "azure,csv,migrate"` consistent matcht ongeacht de
 * brondata-volgorde van de array.
 */
export function canonicalArrayJoin(values: readonly string[]): string {
  return values.map(v => canonicalString(v)).sort().join(',')
}

/** Platformkey = `environment/platformName`. Environment structureel
 *  genormaliseerd (#2151), platformName lowercase. */
export function canonicalPlatformKey(environment: string, platformName: string): string {
  return `${canonicalEnvironment(environment)}/${canonicalString(platformName)}`
}

/** Lowercase alle string-velden van een Compute. Numerieke en source-velden onaangeraakt. */
export function canonicalCompute(c: Compute): Compute {
  const id = canonicalString(c.id)
  const type = canonicalString(c.type)
  const ip = canonicalString(c.ip)
  const os = canonicalString(c.os)
  const datacenter = canonicalString(c.datacenter)
  const region = canonicalString(c.region)
  if (
    id === c.id && type === c.type && ip === c.ip && os === c.os
    && datacenter === c.datacenter && region === c.region
  ) return c
  return { ...c, id, type, ip, os, datacenter, region }
}

/** Lowercase customer/environment/compartment + map computes. */
export function canonicalPlatform(p: Platform): Platform {
  const customer = canonicalString(p.customer)
  const environment = canonicalEnvironment(p.environment)
  const compartment = canonicalString(p.compartment)
  const computes = p.computes.map(canonicalCompute)
  const computesUnchanged = computes.every((c, i) => c === p.computes[i])
  if (
    customer === p.customer && environment === p.environment
    && compartment === p.compartment && computesUnchanged
  ) return p
  return { ...p, customer, environment, compartment, computes }
}

/**
 * Saneert een volledige ImportedData naar lowercase invariant.
 * - Platform-records: customer/environment/compartment lowercase.
 * - Computes: alle string-velden lowercase.
 * - PlatformKey (record-key): hersamengesteld uit lowercase env/platformName.
 *   Bij key-collisions na normalisatie worden computes van de botsende
 *   platforms samengevoegd (eerste wint qua metadata).
 */
export function canonicalImportedData(data: ImportedData): ImportedData {
  let dirty = false
  const result: ImportedData['platforms'] = {}

  for (const [rawKey, platform] of Object.entries(data.platforms)) {
    const canonPlatform = canonicalPlatform(platform)
    // PlatformKey herconstrueren uit environment + originele platformName-segment.
    // Dit borgt dat de key matcht met env/name zoals downstream verwacht.
    const platformName = rawKey.includes('/')
      ? rawKey.split('/').slice(1).join('/')
      : rawKey
    const canonKey = canonicalPlatformKey(canonPlatform.environment, platformName)

    if (canonKey !== rawKey || canonPlatform !== platform) dirty = true

    if (canonKey in result) {
      // Collision na lowercase-normalisatie: computes samenvoegen, metadata van
      // eerste winnaar. Dit kan voorkomen bij bv. 'Prod/X' en 'prod/x' naast elkaar.
      const existing = result[canonKey]
      result[canonKey] = {
        ...existing,
        computes: [...existing.computes, ...canonPlatform.computes],
      }
    } else {
      result[canonKey] = canonPlatform
    }
  }

  if (!dirty) return data
  return { ...data, platforms: result }
}

// ─── Enrichment-data ─────────────────────────────────────────────────────────

function canonicalManualPlatform(p: ManualPlatform): ManualPlatform {
  const name = canonicalString(p.name)
  const customer = canonicalString(p.customer)
  const environment = canonicalEnvironment(p.environment)
  const compartment = p.compartment !== undefined ? canonicalString(p.compartment) : p.compartment
  if (name === p.name && customer === p.customer && environment === p.environment && compartment === p.compartment) {
    return p
  }
  return { ...p, name, customer, environment, compartment }
}

function canonicalManualCustomer(c: ManualCustomer): ManualCustomer {
  const name = canonicalString(c.name)
  if (name === c.name) return c
  return { ...c, name }
}

function canonicalFieldCorrection(c: FieldCorrection): FieldCorrection {
  const originalKey = canonicalString(c.originalKey)
  const customer = c.customer !== undefined ? canonicalString(c.customer) : c.customer
  const environment = c.environment !== undefined ? canonicalEnvironment(c.environment) : c.environment
  const platformName = c.platformName !== undefined ? canonicalString(c.platformName) : c.platformName
  if (
    originalKey === c.originalKey && customer === c.customer
    && environment === c.environment && platformName === c.platformName
  ) return c
  return { ...c, originalKey, customer, environment, platformName }
}

export function canonicalEnrichmentData(data: EnrichmentData): EnrichmentData {
  const platforms: Record<string, ManualPlatform> = {}
  let dirty = false
  for (const [uuid, manual] of Object.entries(data.platforms)) {
    const canon = canonicalManualPlatform(manual)
    if (canon !== manual) dirty = true
    platforms[uuid] = canon
  }
  const customers = data.customers.map(canonicalManualCustomer)
  const customersUnchanged = customers.every((c, i) => c === data.customers[i])
  if (!customersUnchanged) dirty = true

  let corrections = data.corrections
  if (corrections) {
    const canonCorrections = corrections.map(canonicalFieldCorrection)
    if (!canonCorrections.every((c, i) => c === corrections![i])) dirty = true
    corrections = canonCorrections
  }

  if (!dirty) return data
  return { ...data, platforms, customers, corrections }
}

// ─── Dynamic-attribute-data ──────────────────────────────────────────────────

/**
 * Lowercased + deduplicate een lijst attribuut-definities op canonical name.
 *
 * Pre-#1396 disk-state kon zowel `'lastBackup'` als `'lastbackup'` bevatten —
 * na canonical-string krijgen die dezelfde naam. Zonder dedup blijven beide
 * entries in de array, wat React-iteratie (key={attr.name}) duplicate-key
 * warnings oplevert. Latere entry wint per naam (insertion-order van Set
 * is deterministisch).
 *
 * `markDirty` wordt aangeroepen wanneer er íets verandert (rename of dedup),
 * zodat de outer `canonicalDynamicAttributeData` weet of er een nieuwe
 * referentie nodig is.
 */
function dedupAttrsByName<A extends { name: string }>(
  attrs: A[],
  markDirty: () => void,
): A[] {
  const byName = new Map<string, A>()
  for (const a of attrs) {
    const name = canonicalString(a.name)
    if (name !== a.name) markDirty()
    if (byName.has(name)) markDirty()
    byName.set(name, name === a.name ? a : { ...a, name })
  }
  return [...byName.values()]
}

/**
 * Canonicaliseer een platform-scope bucket-key (#2151). Keys zijn óf een UUID
 * (blijft, enkel lowercase) óf een `env/name` platformKey-string — dan moet de
 * env-component structureel genormaliseerd worden (`canonicalEnvironment`), zodat
 * een override die met een oude env-spelling (`1. production/x`) is opgeslagen
 * matcht met het canonieke platform (`production/x`). Anders raakt de override
 * los (orphaned) zodra env genormaliseerd is.
 */
function canonicalPlatformScopeKey(key: string): string {
  const slash = key.indexOf('/')
  if (slash < 0) return canonicalString(key)
  return `${canonicalEnvironment(key.slice(0, slash))}/${canonicalString(key.slice(slash + 1))}`
}

function canonicalAttrValues(
  values: Record<string, Record<string, string>> | undefined,
  keyTransform: (key: string) => string = canonicalString,
): { out: Record<string, Record<string, string>>; dirty: boolean } {
  if (!values) return { out: {}, dirty: false }
  const out: Record<string, Record<string, string>> = {}
  let dirty = false
  for (const [key, attrs] of Object.entries(values)) {
    const canonKey = keyTransform(key)
    if (canonKey !== key) dirty = true
    const canonAttrs: Record<string, string> = {}
    for (const [attrName, val] of Object.entries(attrs)) {
      const canonName = canonicalString(attrName)
      const canonVal = canonicalString(val)
      if (canonName !== attrName || canonVal !== val) dirty = true
      // Bij key-collision (bv. 'Role' en 'role'): laatste wint. Deterministisch
      // omdat Object.entries insertion-order volgt.
      canonAttrs[canonName] = canonVal
    }
    // Bij key-collision in outer map (bv. 'VM1' en 'vm1' als aparte entries):
    // merge attrs; latere entry wint per attribuut.
    out[canonKey] = { ...(out[canonKey] ?? {}), ...canonAttrs }
  }
  return { out, dirty }
}

/**
 * Canonicaliseer de `environment`-*waarde* in elke record van een override-bucket
 * (#2151). Raakt de keys niet aan; enkel het `environment`-veld. Idempotent.
 */
function canonicalizeEnvValueInBucket(
  bucket: Record<string, Record<string, string>>,
): { out: Record<string, Record<string, string>>; dirty: boolean } {
  let dirty = false
  const out: Record<string, Record<string, string>> = {}
  for (const [key, rec] of Object.entries(bucket)) {
    const env = rec['environment']
    if (env === undefined) { out[key] = rec; continue }
    const canon = canonicalEnvironment(env)
    if (canon === env) { out[key] = rec }
    else { out[key] = { ...rec, environment: canon }; dirty = true }
  }
  return { out, dirty }
}

export function canonicalDynamicAttributeData(data: DynamicAttributeData): DynamicAttributeData {
  let dirty = false

  const importAttributes = dedupAttrsByName(data.importAttributes, () => { dirty = true })
  const userAttributes = dedupAttrsByName(data.userAttributes, () => { dirty = true })

  const compute = canonicalAttrValues(data.values.compute)
  // Platform-scope keys zijn `env/name` (of UUID) → env-component structureel
  // canonicaliseren zodat overrides niet orphanen na env-normalisatie (#2151).
  const platform = canonicalAttrValues(data.values.platform, canonicalPlatformScopeKey)
  if (compute.dirty || platform.dirty) dirty = true

  const stdCompute = canonicalAttrValues(data.standardOverrides?.compute)
  const stdPlatform = canonicalAttrValues(data.standardOverrides?.platform, canonicalPlatformScopeKey)
  // Env-override-*waarde* structureel canonicaliseren (#2151), zodat een compute-/
  // platform-override met '1. production' niet naast de canonieke 'production'
  // blijft tonen. Enkel het `environment`-veld; andere velden ongemoeid.
  const stdComputeEnv = canonicalizeEnvValueInBucket(stdCompute.out)
  const stdPlatformEnv = canonicalizeEnvValueInBucket(stdPlatform.out)
  if (stdCompute.dirty || stdPlatform.dirty || stdComputeEnv.dirty || stdPlatformEnv.dirty) dirty = true

  const impOverrideCompute = canonicalAttrValues(data.importAttributeOverrides?.compute)
  if (impOverrideCompute.dirty) dirty = true

  if (!dirty) return data
  return {
    ...data,
    importAttributes,
    userAttributes,
    values: {
      ...data.values,
      compute: compute.out,
      platform: data.values.platform ? platform.out : data.values.platform,
    },
    standardOverrides: data.standardOverrides
      ? { compute: stdComputeEnv.out, platform: stdPlatformEnv.out }
      : data.standardOverrides,
    importAttributeOverrides: data.importAttributeOverrides
      ? { compute: impOverrideCompute.out }
      : data.importAttributeOverrides,
  }
}

/**
 * Lowercased een per-versie `DynamicAttributeSnapshot` (#1216).
 *
 * Snapshots delen het waarden-formaat met `DynamicAttributeData` maar hebben
 * geen `importAttributes`/`userAttributes`/`columnMappedFields` — dus we
 * hergebruiken alleen de waarden-canonicalisering. Wordt toegepast bij het
 * laden van versie-payloads (`unwrapPayload`) zodat oude versies geschreven
 * vóór waarde-canonicalisering in csv-adapter geen case-only diffs meer
 * produceren tegen nieuwe versies.
 */
export function canonicalDynamicAttributeSnapshot(
  snapshot: DynamicAttributeSnapshot,
): DynamicAttributeSnapshot {
  let dirty = false

  const compute = canonicalAttrValues(snapshot.values.compute)
  const platform = canonicalAttrValues(snapshot.values.platform)
  if (compute.dirty || platform.dirty) dirty = true

  const stdCompute = canonicalAttrValues(snapshot.standardOverrides?.compute)
  const stdPlatform = canonicalAttrValues(snapshot.standardOverrides?.platform)
  if (stdCompute.dirty || stdPlatform.dirty) dirty = true

  const impOverrideCompute = canonicalAttrValues(snapshot.importAttributeOverrides?.compute)
  if (impOverrideCompute.dirty) dirty = true

  if (!dirty) return snapshot
  return {
    values: {
      compute: compute.out,
      ...(snapshot.values.platform ? { platform: platform.out } : {}),
    },
    ...(snapshot.standardOverrides
      ? { standardOverrides: { compute: stdCompute.out, platform: stdPlatform.out } }
      : {}),
    ...(snapshot.importAttributeOverrides
      ? { importAttributeOverrides: { compute: impOverrideCompute.out } }
      : {}),
  }
}
